import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Ban,
  Eye,
  KeyRound,
  LockOpen,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import {
  USER_STATUS_LABELS,
  type Paginated,
  type Profile,
  type User,
  type UserStatus,
  type UserSummary,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDateTime, formatPhone, formatRelative, initials } from '@/lib/format';
import { useDepartments, useUnits } from '@/lib/queries';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SummaryCards, type SummaryCard } from '@/components/SummaryCards';
import { SmartDataTable, type Column, type SortState } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Usuários e acessos.
 *
 * Cartões com a contagem por situação (da API, pela mesma regra do filtro),
 * filtros por situação, perfil e equipe, e a lista com as ações à mão: editar,
 * redefinir senha, ver os acessos e o menu com desbloquear / desativar /
 * excluir. Clicar na linha abre o painel lateral com o detalhe.
 *
 * "Convite pendente" é quem foi cadastrado e ainda não entrou; "Bloqueado" é
 * quem errou a senha demais — destrava sozinho no prazo, ou aqui no menu.
 */
const STATUS_KIND: Record<UserStatus, BadgeKind> = {
  ATIVO: 'ok',
  PENDENTE: 'warn',
  BLOQUEADO: 'bad',
  INATIVO: 'mute',
};

export function UserStatusBadge({ status }: { status: UserStatus }) {
  return <StatusBadge kind={STATUS_KIND[status]}>{USER_STATUS_LABELS[status]}</StatusBadge>;
}

/** Cor estável por pessoa: o mesmo nome cai sempre na mesma cor. */
const TONS = ['mare', 'verde', 'ambar', 'roxo', 'rosa', 'azul'] as const;
function tomDe(texto: string) {
  let soma = 0;
  for (const letra of texto) soma = (soma * 31 + letra.charCodeAt(0)) >>> 0;
  return TONS[soma % TONS.length];
}

function Avatar({ name, large }: { name: string; large?: boolean }) {
  return (
    <span className={`us-avatar us-avatar--${tomDe(name)}${large ? ' us-avatar--grande' : ''}`} aria-hidden>
      {initials(name)}
    </span>
  );
}

/** Entrou nos últimos 7 dias = bolinha verde; senão, cinza. */
function UltimoAcesso({ value, agora }: { value: string | null; agora: number }) {
  if (!value) return <span className="us-acesso">nunca</span>;
  const recente = agora - new Date(value).getTime() < 7 * 24 * 60 * 60 * 1000;
  return (
    <span className={`us-acesso${recente ? ' is-recente' : ''}`} title={formatDateTime(value)}>
      {formatRelative(value)}
    </span>
  );
}

// `string` de propósito: o roteador só tipa as rotas de arquivo, e Perfis é
// rota de código (mesmo caso do `caminhoDoRecebimento` em Pedidos).
const PERFIS_PATH: string = '/configuracoes/perfis';

function percentual(parte: number, total: number) {
  return total > 0 ? `${Math.round((parte / total) * 100)}% do total` : '—';
}

export function UsuariosPage() {
  const { can, user: me } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [profileId, setProfileId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState<SortState>({ sort: 'name', direction: 'asc' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<User | 'new' | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [removing, setRemoving] = useState<User | null>(null);
  // "Agora" fixo por abertura da tela: a bolinha de acesso recente não pisca
  // a cada render.
  const [agora] = useState(() => Date.now());

  const query = useQuery({
    queryKey: ['users', { search, status, profileId, departmentId, page, perPage, sort }],
    queryFn: () =>
      api.get<Paginated<User>>('/users', {
        search: search || undefined,
        status: status || undefined,
        profileId: profileId || undefined,
        departmentId: departmentId || undefined,
        page,
        perPage,
        sort: sort.sort,
        direction: sort.direction,
      }),
  });
  const summary = useQuery({
    queryKey: ['users', 'summary'],
    queryFn: () => api.get<UserSummary>('/users/summary'),
  });
  const profiles = useQuery({
    queryKey: ['profiles'],
    queryFn: () => api.get<{ data: Profile[] }>('/profiles'),
    staleTime: 60_000,
  });
  const departments = useDepartments();

  const selected = query.data?.data.find((row) => row.id === selectedId) ?? null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete<User>(`/users/${id}`),
    onSuccess: () => {
      setRemoving(null);
      setSelectedId(null);
      refresh();
    },
  });
  // Ações rápidas do menu "…". O erro aparece no topo da lista — o menu já
  // fechou quando a resposta chega.
  const quickMutation = useMutation({
    mutationFn: ({ user, action }: { user: User; action: 'unlock' | 'activate' | 'deactivate' }) =>
      action === 'unlock'
        ? api.post<User>(`/users/${user.id}/unlock`, {})
        : api.patch<User>(`/users/${user.id}`, { isActive: action === 'activate' }),
    onSuccess: refresh,
  });

  const muda =
    <V,>(setter: (value: V) => void) =>
    (value: V) => {
      setter(value);
      setPage(1);
    };

  const resumo = summary.data;
  const filtraStatus = (alvo: UserStatus) => () => muda(setStatus)(status === alvo ? '' : alvo);
  const cards: SummaryCard[] = [
    {
      key: 'total',
      label: 'Total de usuários',
      value: String(resumo?.total ?? 0),
      hint: resumo?.newLast30Days
        ? `+${resumo.newLast30Days} nos últimos 30 dias`
        : 'Nenhum novo nos últimos 30 dias',
    },
    {
      key: 'ativos',
      label: 'Usuários ativos',
      value: String(resumo?.active ?? 0),
      hint: percentual(resumo?.active ?? 0, resumo?.total ?? 0),
      active: status === 'ATIVO',
      onClick: filtraStatus('ATIVO'),
    },
    {
      key: 'pendentes',
      label: 'Convites pendentes',
      value: String(resumo?.pending ?? 0),
      hint: 'Cadastrados que ainda não entraram',
      tone: 'atencao',
      apagarSeZero: true,
      active: status === 'PENDENTE',
      onClick: filtraStatus('PENDENTE'),
    },
    {
      key: 'inativos',
      label: 'Usuários inativos',
      value: String(resumo?.inactive ?? 0),
      hint: percentual(resumo?.inactive ?? 0, resumo?.total ?? 0),
      active: status === 'INATIVO',
      onClick: filtraStatus('INATIVO'),
    },
  ];
  // Bloqueado é exceção: só ganha cartão quando existe.
  if ((resumo?.blocked ?? 0) > 0) {
    cards.push({
      key: 'bloqueados',
      label: 'Bloqueados',
      value: String(resumo?.blocked ?? 0),
      hint: 'Erraram a senha demais',
      tone: 'ruim',
      active: status === 'BLOQUEADO',
      onClick: filtraStatus('BLOQUEADO'),
    });
  }

  /** Ícone de ação da linha. O clique faz só a ação, não abre o painel. */
  const acao = (label: string, icon: typeof Pencil, onClick: () => void) => {
    const Icon = icon;
    return (
      <button
        type="button"
        className="nf-acao"
        aria-label={label}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <Icon size={15} aria-hidden />
      </button>
    );
  };

  const columns: Array<Column<User>> = [
    {
      key: 'name',
      label: 'Nome',
      sortable: true,
      render: (user) => (
        <span className="us-pessoa">
          <Avatar name={user.name} />
          <span>
            <span className="us-pessoa__nome">{user.name}</span>
            {user.jobTitle && <span className="us-pessoa__cargo">{user.jobTitle}</span>}
          </span>
        </span>
      ),
    },
    { key: 'email', label: 'E-mail', sortable: true, render: (user) => user.email },
    {
      key: 'profiles',
      label: 'Perfil',
      render: (user) =>
        user.profiles.length === 0 ? (
          '—'
        ) : (
          <span className="us-perfis">
            {user.profiles.map((profile) => (
              <span key={profile.id} className={`us-perfil us-perfil--${tomDe(profile.slug)}`}>
                {profile.name}
              </span>
            ))}
          </span>
        ),
    },
    { key: 'department', label: 'Equipe / Setor', render: (user) => blank(user.department?.name) },
    {
      key: 'lastLoginAt',
      label: 'Último acesso',
      sortable: true,
      render: (user) => <UltimoAcesso value={user.lastLoginAt} agora={agora} />,
    },
    { key: 'status', label: 'Status', render: (user) => <UserStatusBadge status={user.status} /> },
    {
      key: 'acoes',
      label: 'Ações',
      render: (user) => (
        // Enter/Espaço num botão daqui não pode chegar à linha: o handler dela
        // cancela a tecla e abre o painel, e o botão nunca seria acionado.
        <div className="nf-acoes" onKeyDown={(event) => event.stopPropagation()}>
          {can('usuarios:editar') && acao('Editar usuário', Pencil, () => setFormFor(user))}
          {can('usuarios:editar') && acao('Redefinir senha', KeyRound, () => setResetting(user))}
          {acao('Ver acessos', Eye, () => setSelectedId(user.id))}
          {(can('usuarios:editar') || can('usuarios:excluir')) && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                className="nf-acao"
                aria-label="Mais ações"
                title="Mais ações"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHorizontal size={15} aria-hidden />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="menu"
                  align="end"
                  sideOffset={6}
                  onClick={(event) => event.stopPropagation()}
                >
                  {can('usuarios:editar') && user.status === 'BLOQUEADO' && (
                    <DropdownMenu.Item
                      className="menu__item"
                      onSelect={() => quickMutation.mutate({ user, action: 'unlock' })}
                    >
                      <LockOpen size={15} aria-hidden /> Desbloquear
                    </DropdownMenu.Item>
                  )}
                  {can('usuarios:editar') &&
                    (user.isActive ? (
                      <DropdownMenu.Item
                        className="menu__item"
                        disabled={user.id === me?.id}
                        onSelect={() => quickMutation.mutate({ user, action: 'deactivate' })}
                      >
                        <Ban size={15} aria-hidden /> Desativar
                      </DropdownMenu.Item>
                    ) : (
                      <DropdownMenu.Item
                        className="menu__item"
                        onSelect={() => quickMutation.mutate({ user, action: 'activate' })}
                      >
                        <RotateCcw size={15} aria-hidden /> Reativar
                      </DropdownMenu.Item>
                    ))}
                  {can('usuarios:excluir') && user.id !== me?.id && (
                    <>
                      <DropdownMenu.Separator className="menu__sep" />
                      <DropdownMenu.Item className="menu__item" onSelect={() => setRemoving(user)}>
                        <Trash2 size={15} aria-hidden /> Excluir
                      </DropdownMenu.Item>
                    </>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      ),
    },
  ];

  const hasFilters = Boolean(search || status || profileId || departmentId);
  const limpar = () => {
    setSearch('');
    setStatus('');
    setProfileId('');
    setDepartmentId('');
    setPage(1);
  };

  return (
    <>
      <WorkspaceHeader
        title="Usuários e acessos"
        description="Usuários do sistema, seus perfis de acesso e permissões."
        actions={
          <>
            {can('perfis:ver') && (
              <Link to={PERFIS_PATH} className="btn btn--outline">
                <ShieldCheck size={15} aria-hidden />
                Perfis de acesso
              </Link>
            )}
            {can('usuarios:criar') && (
              <Button variant="primary" icon={Plus} onClick={() => setFormFor('new')}>
                Novo usuário
              </Button>
            )}
          </>
        }
      />

      <div className="workspace__body">
        <SummaryCards cards={cards} loading={summary.isPending} />

        <FilterBar
          placeholder="Buscar por nome ou e-mail…"
          search={search}
          onSearchChange={muda(setSearch)}
          filters={[
            {
              key: 'status',
              label: 'Status',
              value: status,
              options: (Object.keys(USER_STATUS_LABELS) as UserStatus[]).map((value) => ({
                value,
                label: USER_STATUS_LABELS[value],
              })),
              onChange: (value) => muda(setStatus)(value as UserStatus | ''),
            },
            {
              key: 'perfil',
              label: 'Perfil de acesso',
              value: profileId,
              options: (profiles.data?.data ?? []).map((profile) => ({
                value: profile.id,
                label: profile.name,
              })),
              onChange: muda(setProfileId),
            },
            {
              key: 'equipe',
              label: 'Equipe / Setor',
              value: departmentId,
              options: (departments.data ?? []).map((department) => ({
                value: department.id,
                label: department.name,
              })),
              onChange: muda(setDepartmentId),
            },
          ]}
        >
          {hasFilters && (
            <button type="button" className="nf-limpar" onClick={limpar}>
              Limpar filtros
            </button>
          )}
        </FilterBar>

        {quickMutation.isError && (
          <div className="login__alert" role="alert" style={{ marginBottom: 12 }}>
            {errorMessage(quickMutation.error)}
          </div>
        )}

        {query.isPending && <TableSkeleton columns={7} />}

        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {query.data && (
          <div className="nf-lista">
            <SmartDataTable
              caption="Usuários da plataforma"
              columns={columns}
              rows={query.data.data}
              meta={query.data.meta}
              selectedId={selectedId}
              onRowClick={(row) => setSelectedId(row.id)}
              onPageChange={setPage}
              perPage={perPage}
              onPerPageChange={(value) => {
                setPerPage(value);
                setPage(1);
              }}
              sortState={sort}
              onSortChange={(next) => {
                setSort(next);
                setPage(1);
              }}
              emptyState={
                hasFilters ? (
                  <NoResultsState onClear={limpar} />
                ) : (
                  <EmptyState
                    title="Nenhum usuário cadastrado"
                    description="Os operadores criados aparecem aqui."
                  />
                )
              }
            />
          </div>
        )}
      </div>

      <ContextPanel
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name ?? ''}
        subtitle={selected?.email}
        tabs={['Dados', 'Acesso']}
        footer={
          selected ? (
            <>
              {can('usuarios:editar') && (
                <Button small variant="primary" icon={Pencil} onClick={() => setFormFor(selected)}>
                  Editar
                </Button>
              )}
              {can('usuarios:editar') && (
                <Button small icon={KeyRound} onClick={() => setResetting(selected)}>
                  Redefinir senha
                </Button>
              )}
              {can('usuarios:excluir') && selected.id !== me?.id && (
                <Button small variant="danger" icon={Trash2} onClick={() => setRemoving(selected)}>
                  Excluir
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {(tab) =>
          !selected ? null : tab === 0 ? (
            <>
              <ReadField label="Nome" value={selected.name} />
              <ReadField label="E-mail" value={selected.email} />
              <ReadField label="Telefone" value={formatPhone(selected.phone)} mono />
              <ReadField label="Cargo" value={blank(selected.jobTitle)} />
              <ReadField label="Equipe / Setor" value={blank(selected.department?.name)} />
              <ReadField
                label="Perfis"
                value={selected.profiles.map((profile) => profile.name).join(' · ') || '—'}
              />
              <ReadField label="Situação" value={<UserStatusBadge status={selected.status} />} />
              {selected.notes && <ReadField label="Observações" value={selected.notes} />}
            </>
          ) : (
            <>
              <ReadField
                label="Último acesso"
                value={selected.lastLoginAt ? formatDateTime(selected.lastLoginAt) : 'Nunca entrou'}
                mono
              />
              {selected.status === 'BLOQUEADO' && selected.lockedUntil && (
                <ReadField
                  label="Bloqueado até"
                  value={formatDateTime(selected.lockedUntil)}
                  mono
                />
              )}
              <ReadField
                label="Troca de senha no próximo acesso"
                value={selected.mustChangePassword ? 'Sim' : 'Não'}
              />
              <ReadField label="Criado em" value={formatDateTime(selected.createdAt)} mono />
              <ReadField label="Atualizado em" value={formatDateTime(selected.updatedAt)} mono />
            </>
          )
        }
      </ContextPanel>

      <UserFormModal
        open={formFor !== null}
        user={formFor === 'new' ? null : formFor}
        profiles={(profiles.data?.data ?? []).filter((profile) => profile.isActive)}
        onClose={() => setFormFor(null)}
        onSaved={() => {
          setFormFor(null);
          refresh();
        }}
      />

      {resetting && (
        <ResetPasswordModal
          open
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => {
            setResetting(null);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        destructive
        loading={removeMutation.isPending}
        title={removing ? `Excluir ${removing.name}?` : ''}
        confirmLabel="Excluir usuário"
        message={
          <>
            As sessões abertas são encerradas na hora e o acesso é cortado. O
            histórico do que essa pessoa fez continua na auditoria.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

const NOTAS_MAX = 500;

/**
 * Novo / editar usuário — janela no centro, no desenho do Diretor.
 *
 * O perfil é um só na tela. Quem já tem mais de um (cadastro antigo) continua
 * com os outros: a linha embaixo do campo mostra quais, e salvar não os tira.
 */
function UserFormModal({
  open,
  user,
  profiles,
  onClose,
  onSaved,
}: {
  open: boolean;
  user: User | null;
  profiles: Profile[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const departments = useDepartments();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [profileId, setProfileId] = useState('');
  // Perfis além do principal (cadastro antigo com mais de um). Ficam até
  // alguém tirar no "x" — salvar não pode nem apagar nem manter escondido.
  const [extraIds, setExtraIds] = useState<string[]>([]);
  const [unitId, setUnitId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (user?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setName(user?.name ?? '');
    setEmail(user?.email ?? '');
    setPhone(user?.phone ? formatPhone(user.phone) : '');
    setJobTitle(user?.jobTitle ?? '');
    setDepartmentId(user?.departmentId ?? '');
    setProfileId(user?.profiles[0]?.id ?? '');
    setExtraIds(user?.profiles.slice(1).map((profile) => profile.id) ?? []);
    setUnitId(user?.unitId ?? '');
    setIsActive(user?.isActive ?? true);
    setMustChangePassword(user?.mustChangePassword ?? true);
    setPassword('');
    setNotes(user?.notes ?? '');
  }

  const units = useUnits();
  const outrosPerfis = (user?.profiles ?? []).filter(
    (profile) => extraIds.includes(profile.id) && profile.id !== profileId,
  );

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      user ? api.patch<User>(`/users/${user.id}`, body) : api.post<User>('/users', body),
    onSuccess: onSaved,
  });
  const errors = fieldErrors(mutation.error);

  const canSubmit =
    name.trim().length >= 2 &&
    email.trim().length >= 5 &&
    Boolean(profileId) &&
    (user ? true : password.length >= 10);

  const salvar = () =>
    mutation.mutate({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      jobTitle: jobTitle.trim() || null,
      departmentId: departmentId || null,
      notes: notes.trim() || null,
      profileIds: [profileId, ...outrosPerfis.map((profile) => profile.id)],
      unitId: unitId || null,
      isActive,
      mustChangePassword,
      ...(user ? {} : { password }),
    });

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={user ? 'Editar usuário' : 'Novo usuário'}
      description={
        user
          ? 'Atualize as informações, o perfil de acesso e a situação do usuário.'
          : 'A pessoa entra com a senha inicial e troca no primeiro acesso.'
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={user ? Save : Plus}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={salvar}
          >
            {user ? 'Salvar alterações' : 'Criar usuário'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      {user && (
        <div className="us-cartao">
          <Avatar name={user.name} large />
          <div>
            <div className="us-cartao__nome">{user.name}</div>
            <div className="us-cartao__linha">
              {user.jobTitle && <span>{user.jobTitle}</span>}
              <UserStatusBadge status={user.status} />
            </div>
          </div>
        </div>
      )}

      <div className="us-form">
        <Field label="Nome completo" htmlFor="us-name" required error={errors.name}>
          <input id="us-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="E-mail" htmlFor="us-email" required error={errors.email}>
          <input
            id="us-email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field label="Telefone" htmlFor="us-phone" error={errors.phone}>
          <div className="us-icone">
            <Phone size={15} aria-hidden />
            <input
              id="us-phone"
              className="input input--mono"
              value={phone}
              placeholder="(11) 98765-4321"
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </Field>
        <Field label="Cargo" htmlFor="us-cargo" error={errors.jobTitle}>
          <input
            id="us-cargo"
            className="input"
            value={jobTitle}
            maxLength={80}
            placeholder="Ex.: Comprador"
            onChange={(e) => setJobTitle(e.target.value)}
          />
        </Field>

        <Field
          label="Equipe / Setor"
          htmlFor="us-equipe"
          error={errors.departmentId}
          hint={
            departments.data && departments.data.length === 0
              ? 'Nenhuma cadastrada — crie em RH → Departamentos e cargos.'
              : undefined
          }
        >
          <select
            id="us-equipe"
            className="input"
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Sem equipe</option>
            {(departments.data ?? []).map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Perfil de acesso"
          htmlFor="us-perfil"
          required
          error={errors.profileIds}
        >
          <select
            id="us-perfil"
            className="input"
            value={profileId}
            onChange={(e) => setProfileId(e.target.value)}
          >
            <option value="">Escolha o perfil</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
          {outrosPerfis.length > 0 && (
            <div className="us-extras">
              <span>Também tem:</span>
              {outrosPerfis.map((profile) => (
                <span key={profile.id} className="us-extra">
                  {profile.name}
                  <button
                    type="button"
                    aria-label={`Tirar o perfil ${profile.name}`}
                    title="Tirar este perfil"
                    onClick={() => setExtraIds((ids) => ids.filter((id) => id !== profile.id))}
                  >
                    <X size={12} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>

        <Field label="Status" htmlFor="us-status" required>
          <select
            id="us-status"
            className="input"
            value={isActive ? 'ATIVO' : 'INATIVO'}
            onChange={(e) => setIsActive(e.target.value === 'ATIVO')}
          >
            <option value="ATIVO">Ativo — pode entrar</option>
            <option value="INATIVO">Inativo — acesso cortado</option>
          </select>
        </Field>
        <div className="us-troca">
          <div>
            <div className="us-troca__titulo" id="us-troca-rotulo">
              Exigir troca de senha no próximo acesso
            </div>
            <div className="us-troca__desc">
              O usuário será obrigado a alterar a senha no próximo login.
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={mustChangePassword}
            aria-labelledby="us-troca-rotulo"
            className={`us-switch${mustChangePassword ? ' is-on' : ''}`}
            onClick={() => setMustChangePassword((v) => !v)}
          >
            <span />
          </button>
        </div>

        <Field label="Unidade" htmlFor="us-unit" hint="Onde a pessoa trabalha fixo. Opcional.">
          <select
            id="us-unit"
            className="input"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">Sem unidade fixa</option>
            {(units.data ?? []).map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.code} · {unit.name}
              </option>
            ))}
          </select>
        </Field>

        {!user && (
          <Field
            label="Senha inicial"
            htmlFor="us-password"
            required
            error={errors.password}
            hint="Mínimo 10 caracteres com letras e números."
          >
            <input
              id="us-password"
              className="input input--mono"
              type="text"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}

        <div className="us-form__inteira">
          <Field label="Observações" htmlFor="us-notes" error={errors.notes}>
            <textarea
              id="us-notes"
              className="input"
              rows={3}
              maxLength={NOTAS_MAX}
              value={notes}
              placeholder="Adicione alguma observação sobre este usuário…"
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
          <div className="nf-contador">
            {notes.length}/{NOTAS_MAX}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({
  open,
  user,
  onClose,
  onDone,
}: {
  open: boolean;
  user: User;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPassword('');
  }

  const mutation = useMutation({
    mutationFn: () => api.post<User>(`/users/${user.id}/reset-password`, { password }),
    onSuccess: onDone,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Redefinir senha de ${user.name}`}
      description="A pessoa é obrigada a trocar esta senha no próximo acesso"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={KeyRound}
            loading={mutation.isPending}
            disabled={password.length < 10}
            onClick={() => mutation.mutate()}
          >
            Redefinir senha
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field
          label="Senha temporária"
          htmlFor="rp-password"
          error={errors.password}
          hint="Mínimo 10 caracteres com letras e números"
        >
          <input
            id="rp-password"
            className="input input--mono"
            type="text"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
